import { ClipboardIcon, MoreVerticalIcon, UploadIcon } from "vega-icons";

import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { SegmentedTabs } from "@vega-core/components/SegmentedTabs/SegmentedTabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@vega-core/components/shadcn/dropdown-menu";
import { m } from "@vega-core/paraglide/messages";
import { getIsGPCoInvestPortal } from "@vega-core/utils/global";
import { useFeatureFlagGuard } from "@vega-modules/hooks/useFeatureFlagGuard";
import { CommonPaths } from "@vega-modules/paths";

import { CoInvestmentPageHeader } from "../components/CoInvestmentPageHeader/CoInvestmentPageHeader";
import { ShareApprovedTable } from "../components/ShareApprovedTable/ShareApprovedTable";
import { SharePendingTable } from "../components/SharePendingTable/SharePendingTable";
import {
  OPPORTUNITIES_CSV_UPLOAD_INPUT_ID,
  PROSPECTS_CSV_UPLOAD_INPUT_ID,
} from "../constants/prospectsUpload";
import { ShareTabType } from "../constants/sharePageConstants";
import { useOutreachSentLiveRefresh } from "../hooks/useOutreachSentLiveRefresh";
import { useShareManagement } from "../hooks/useShareManagement";

export const SharePage = () => {
  useFeatureFlagGuard(
    "gp_co_investment",
    getIsGPCoInvestPortal() ? undefined : CommonPaths.Home,
  );

  const { featureFlag } = useFeatureFlag();
  useOutreachSentLiveRefresh({
    enabled: featureFlag[FeatureFlag.COINVEST_OUTREACH_EMAIL_VD_27885],
  });

  const {
    dealName,
    activeTab,
    setActiveTab,
    approvedTotalCount,
    pendingTotalCount,
    canAddShortlistItems,
    canImportProspects,
    canEditDealOpportunity,
    canApproveDealShortlist,
    handleFindMoreProspects,
    clearSelectionTrigger,
    handleProspectsUploadClick,
    handleProspectsFileUpload,
    handleOpportunitiesUploadClick,
    handleOpportunitiesFileUpload,
    handleDownloadProspectsCsvTemplate,
    handleDownloadOpportunitiesCsvTemplate,
  } = useShareManagement();

  return (
    <div className="mt-1 flex flex-col gap-4">
      <CoInvestmentPageHeader
        action={
          canImportProspects ? (
            <div className="flex gap-4 items-center flex-wrap">
              <ThemedButton
                dataTestId="find-more-prospects-button"
                onClick={handleFindMoreProspects}
                size="small"
                variant="primary"
              >
                {m.coInvestments_findProspectsFromSalesforce()}
              </ThemedButton>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <ThemedButton
                    ariaLabel="More actions"
                    className="h-10 w-10"
                    dataTestId="share-actions-menu"
                    icon={<MoreVerticalIcon />}
                    variant="secondary"
                  />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="p-2">
                  <DropdownMenuItem onSelect={handleProspectsUploadClick}>
                    <UploadIcon className="w-4 h-4" />
                    <p className="body-base-medium">
                      {m.coInvestments_uploadProspectsCsv()}
                    </p>
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={handleOpportunitiesUploadClick}>
                    <UploadIcon className="w-4 h-4" />
                    <p className="body-base-medium">
                      {m.coInvestments_uploadOpportunitiesCsv()}
                    </p>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={handleDownloadProspectsCsvTemplate}
                  >
                    <ClipboardIcon className="w-4 h-4" />
                    <p className="body-base-medium">
                      {m.coInvestments_downloadProspectsTemplate()}
                    </p>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={handleDownloadOpportunitiesCsvTemplate}
                  >
                    <ClipboardIcon className="w-4 h-4" />
                    <p className="body-base-medium">
                      {m.coInvestments_downloadOpportunitiesTemplate()}
                    </p>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : undefined
        }
        capitalize={false}
        closedDealNoticeMessage={m.coInvestments_dealClosed_noticeShare()}
        fundName={dealName}
        showClosedDealNotice
        title={m.coInvestments_nav_share()}
      />

      <input
        accept=".csv"
        className="hidden"
        data-testid={PROSPECTS_CSV_UPLOAD_INPUT_ID}
        id={PROSPECTS_CSV_UPLOAD_INPUT_ID}
        onChange={handleProspectsFileUpload}
        type="file"
      />
      <input
        accept=".csv"
        className="hidden"
        data-testid={OPPORTUNITIES_CSV_UPLOAD_INPUT_ID}
        id={OPPORTUNITIES_CSV_UPLOAD_INPUT_ID}
        onChange={handleOpportunitiesFileUpload}
        type="file"
      />

      <SegmentedTabs
        activeTab={activeTab}
        noWrapText
        setActiveTab={setActiveTab}
        tabs={[
          {
            id: ShareTabType.Approved,
            label:
              approvedTotalCount === undefined
                ? m.common_approved()
                : m.coInvestments_share_tab_approved({
                    count: approvedTotalCount,
                  }),
          },
          {
            id: ShareTabType.Pending,
            label:
              pendingTotalCount === undefined
                ? m.common_pending()
                : m.coInvestments_share_tab_pending({
                    count: pendingTotalCount,
                  }),
          },
        ]}
      />

      {activeTab === ShareTabType.Approved ? (
        <div className="mb-48">
          <ShareApprovedTable
            canAddShortlistItems={canAddShortlistItems}
            canEditDealOpportunity={canEditDealOpportunity}
          />
        </div>
      ) : (
        <div className="mb-48">
          <SharePendingTable
            canAddShortlistItems={canAddShortlistItems}
            canApprove={canApproveDealShortlist}
            clearSelectionTrigger={clearSelectionTrigger}
            onFindFromSalesforce={handleFindMoreProspects}
            onUploadClick={handleProspectsUploadClick}
          />
        </div>
      )}
    </div>
  );
};
